import { Button, PlusIcon, Text } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import { FollowUpActionStatusEnum } from '@lmc/validations'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { StepEntryList, useEntryDrawer } from '../../StepEntryCard'
import { useTeamMemberOptions } from '../../stepOptions'
import { ACTION_TEMPLATES, type ActionTemplate } from './actionTemplates'
import FollowUpActionCard from './FollowUpActionCard'
import FollowUpActionDrawer from './FollowUpActionDrawer'
import type { FollowUpActionValue } from './followUpActionValues'

type FollowUpActionListProps = {
  /** Injected by the antd `Form.Item` holding the list. */
  value?: ReadonlyArray<FollowUpActionValue>
  onChange?: (actions: Array<FollowUpActionValue>) => void
  /** Interpolated into the template copy. */
  templateValues: { carePlan: string; name: string }
  testId: string
}

const newAction = (
  overrides: Partial<FollowUpActionValue> = {}
): FollowUpActionValue => ({
  assignedUserId: null,
  completedAt: null,
  details: '',
  dueDate: null,
  status: FollowUpActionStatusEnum.notStarted,
  title: '',
  ...overrides
})

/**
 * The case's actions as cards, with Add action and the template list below.
 * Adding, editing and starting from a template all happen in a drawer.
 */
const FollowUpActionList = ({
  value = [],
  onChange,
  templateValues,
  testId
}: FollowUpActionListProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const ownerOptions = useTeamMemberOptions({
    saved: value.map((action) => action.assignedUserId)
  })
  const { close, drawer, open, remove, save } = useEntryDrawer(value, onChange)

  const addFromTemplate = (template: ActionTemplate) =>
    open(
      newAction({
        details: t(template.detailsKey, templateValues),
        title: t(template.titleKey, templateValues)
      })
    )

  return (
    <List>
      <StepEntryList
        addLabel={t(incidentReportsConstants.CASE_V2_ACTIONS_ADD_ACTION)}
        addTestId={`${testId}-add`}
        onAdd={() => open(newAction())}
      >
        {value.map((action, index) => (
          <FollowUpActionCard
            // A new action has no id until saved, so rows are keyed by place.
            // biome-ignore lint/suspicious/noArrayIndexKey: no id before save
            key={index}
            action={action}
            ownerName={
              ownerOptions.find(
                (option) => option.value === action.assignedUserId
              )?.label
            }
            onDelete={() => remove(index)}
            onEdit={() => open(action, index)}
            testId={`${testId}-action-${index}`}
          />
        ))}
      </StepEntryList>

      <Templates data-testid={`${testId}-templates`}>
        <div>
          <Text variant="body-sm" weight="medium" tone="secondary">
            {t(incidentReportsConstants.CASE_V2_ACTIONS_TEMPLATES)}
          </Text>
          <Text variant="body-sm" tone="secondary">
            {t(incidentReportsConstants.CASE_V2_ACTIONS_TEMPLATES_HINT)}
          </Text>
        </div>
        <ul>
          {ACTION_TEMPLATES.map((template) => (
            <li key={template.id}>
              <Text weight="medium">
                {t(template.titleKey, templateValues)}
              </Text>
              <Button
                variant="transparent"
                icon={<PlusIcon size={16} />}
                aria-label={t(
                  incidentReportsConstants.CASE_V2_ACTIONS_ADD_TEMPLATE,
                  { title: t(template.titleKey, templateValues) }
                )}
                data-testid={`${testId}-template-${template.id}`}
                onClick={() => addFromTemplate(template)}
              >
                {t(incidentReportsConstants.CASE_V2_ACTIONS_ADD)}
              </Button>
            </li>
          ))}
        </ul>
      </Templates>

      {drawer && (
        <FollowUpActionDrawer
          action={drawer.entry}
          isNew={drawer.index === undefined}
          onClose={close}
          onSubmit={save}
          testId={`${testId}-drawer`}
        />
      )}
    </List>
  )
}

const { color, radius, spacing } = cssVars

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[200]};
`

const Templates = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[50]};
  padding: ${spacing[200]} ${spacing[200]} 0;
  border: 1px solid ${color.border.subtle};
  border-radius: ${radius[100]};

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: ${spacing[200]};
    padding: ${spacing[100]} 0;
    border-bottom: 1px solid ${color.border.subtle};
  }

  li:last-child {
    border-bottom: 0;
  }
`

export default FollowUpActionList
